"use client";

import { BookADemoModal } from "@/app/(public)/shared/components/BookADemoModal";
import nowadaysLogo from "@/app/(public)/shared/assets/nowadayslogo.png";
import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import { Button } from "@/app/(public)/shared/ui/Button";
import { BorderBeam } from "@/app/(public)/shared/ui/RunningBorder";
import { Phone, SparksSolid } from "iconoir-react";
import { motion } from "motion/react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";

// Mobile-specific feature components
const MobileEmailFeature = () => {
  return (
    <div className="absolute top-[75%] left-[75%] transform -translate-x-1/2 -translate-y-1/2 scale-[1] origin-center">
      <div className="w-[380px] p-8 bg-white rounded-2xl flex flex-col gap-5 overflow-hidden relative">
        <div className="absolute inset-0 rounded-2xl z-10">
          <BorderBeam
            size={300}
            duration={12}
            borderWidth={2}
            className="from-transparent via-blue-500 to-transparent"
          />
          <BorderBeam
            size={300}
            duration={12}
            delay={6}
            borderWidth={2}
            className="from-transparent via-purple-500 to-transparent"
          />
        </div>
        <div className="relative z-20 flex items-center gap-2">
          <SparksSolid className="w-5 h-5 text-indigo-600" />
          <div className="text-indigo-600 text-tag-md">AI-Generated</div>
        </div>
        <div className="relative z-20 flex flex-col gap-3">
          <div className="text-neutral-400 text-tag-md">To: reservations@hotel.com</div>
          <div className="text-neutral-400 text-tag-md">Subject: Corporate Retreat Inquiry</div>
        </div>
        <div className="relative z-20 flex flex-col gap-2">
          <div className="text-neutral-600 text-body">
            Hi sales team! We're an IATA-certified travel agency, and I have a client who is interested in doing an
            event at Example Hotel. We're looking for commissionable rates. Could you share availability and a proposal?
            Please see the details below and fill out the requested information:
          </div>
          <div className="w-full h-32 absolute left-0 top-[100px] bg-linear-to-b from-white/0 to-white z-30" />
        </div>
      </div>
    </div>
  );
};

const MobilePhoneFeature = () => {
  return (
    <div className="absolute top-[75%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 scale-[0.85] origin-center">
      <div className="w-[420px] h-[500px] p-8 rounded-2xl overflow-visible relative">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <div className="w-full flex flex-col gap-3">
              <div className="flex justify-between items-end">
                <div className="flex items-center gap-2">
                  <SparksSolid className="w-4 h-4 text-indigo-600" />
                  <div className="text-indigo-600 text-tag-sm">Nowadays AI</div>
                </div>
                <div className="text-stone-300 text-tag-sm">00:01</div>
              </div>
              <div className="px-6 py-4 bg-white/80 rounded-xl flex flex-col gap-3 overflow-hidden">
                <div className="text-landing-primary text-body">
                  Hi, my name is Victor. I sent over an RFP yesterday and was wondering if your team received it.
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="w-full flex flex-col gap-3">
              <div className="flex justify-between items-end">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-neutral-400" />
                  <div className="text-landing-primary text-tag-sm">Hotel Staff</div>
                </div>
                <div className="text-stone-300 text-tag-sm">00:05</div>
              </div>
              <div className="px-6 py-4 bg-white/80 rounded-xl flex flex-col gap-3 overflow-hidden">
                <div className="text-landing-primary text-body">
                  No worries, let me take a look! What email did you send it to?
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="w-full flex flex-col gap-3">
              <div className="flex justify-between items-end">
                <div className="flex items-center gap-2">
                  <SparksSolid className="w-4 h-4 text-indigo-600" />
                  <div className="text-indigo-600 text-tag-sm">Nowadays AI</div>
                </div>
                <div className="text-stone-300 text-tag-sm">00:12</div>
              </div>
              <div className="px-6 py-4 bg-white/80 rounded-xl flex flex-col gap-3 overflow-hidden">
                <div className="text-landing-primary text-body">Thanks! I sent it to reservations@hotel.com</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MobileProposalFeature = () => {
  return (
    <div className="absolute top-0 left-0 w-full h-full flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4 relative">
        <div className="w-full max-w-[320px] bg-white/90 rounded-xl p-4 relative mt-36">
          <div className="absolute inset-0 rounded-xl z-10">
            <BorderBeam
              size={200}
              duration={12}
              delay={6}
              borderWidth={2}
              className="from-transparent via-purple-500 to-transparent"
            />
          </div>
          <div className="relative z-20">
            <div className="flex items-center gap-2 mb-3">
              <SparksSolid className="w-4 h-4 text-indigo-600" />
              <div className="text-xs text-indigo-600">Nowadays Breakdown</div>
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-2xl font-bold text-landing-primary">$174,221.91</div>
                <div className="text-xs text-landing-secondary">Total after taxes & fees</div>
              </div>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-landing-secondary">Guest Rooms</span>
                  <span className="text-landing-primary font-medium">$142,685</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-landing-secondary">Meeting Rooms</span>
                  <span className="text-landing-primary font-medium">$18,450</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-landing-secondary">Food & Beverage</span>
                  <span className="text-landing-primary font-medium">$13,086</span>
                </div>
                <div className="h-px bg-gray-200 my-2"></div>
                <div className="flex justify-between font-semibold">
                  <span className="text-landing-primary">Total</span>
                  <span className="text-landing-primary">$174,221.91</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface FeatureSectionProps {
  title: string;
  description: string;
  buttonText: string;
  leftOffset?: string;
  onButtonClick?: () => void;
}

const FeatureSection = ({ title, description, buttonText, onButtonClick }: FeatureSectionProps) => {
  return (
    <div className="group w-full lg:flex-1 h-[500px] p-4 md:p-6 relative overflow-hidden transition-all duration-300 ease-in-out rounded-xl bg-landing-secondary md:hover:bg-landing-primary">
      <div className={`relative inline-flex flex-col justify-start items-start gap-4 md:gap-6 z-10`}>
        <div className="flex flex-col gap-1">
          <div className="w-full text-tag-lg text-landing-primary">{title}</div>
          <div className="w-full text-body text-landing-secondary">{description}</div>
        </div>
        <Button variant="primary" color="primary" size="sm" onClick={onButtonClick}>
          {buttonText}
        </Button>
      </div>

      {/* Email preview for AI Email Outreach section */}
      {title === "AI email outreach" && (
        <>
          {/* Mobile version */}
          <div className="block md:hidden">
            <MobileEmailFeature />
          </div>
          {/* Desktop version */}
          <div className="hidden md:block">
            <div className="absolute top-[75%] left-2/3 transform -translate-x-1/2 -translate-y-1/2 scale-[0.65] origin-center group-hover:-rotate-3 transition-transform duration-300 ease-in-out">
              <div className="w-[460px] p-10 bg-white rounded-2xl flex flex-col gap-6 overflow-hidden relative">
                <div className="absolute inset-0 rounded-2xl z-10">
                  <BorderBeam
                    size={300}
                    duration={12}
                    borderWidth={2}
                    className="from-transparent via-blue-500 to-transparent"
                  />
                  <BorderBeam
                    size={300}
                    duration={12}
                    delay={6}
                    borderWidth={2}
                    className="from-transparent via-purple-500 to-transparent"
                  />
                </div>
                <div className="relative z-20 flex items-center gap-2">
                  <SparksSolid className="w-6 h-6 text-indigo-600" />
                  <div className="text-indigo-600 text-tag-lg">AI-Generated</div>
                </div>
                <div className="relative z-20 flex flex-col gap-4">
                  <div className="text-neutral-400 text-tag-lg">To: reservations@hotel.com</div>
                  <div className="text-neutral-400 text-tag-lg">Subject: Corporate Retreat Inquiry</div>
                </div>
                <div className="relative z-20 flex flex-col gap-2.5">
                  <div className="text-neutral-600 text-body-lg">
                    Hi sales team! We're an IATA-certified travel agency, and I have a client who is interested in doing
                    an event at Example Hotel. We're looking for commissionable rates. Could you share availability and
                    a proposal? Please see the details below and fill out the requested information:
                  </div>
                  <div className="w-[545px] h-40 absolute left-0 top-[135px] bg-linear-to-b from-white/0 to-white z-30" />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Phone call preview for AI Phone Calls section */}
      {title === "AI phone calls" && (
        <>
          {/* Mobile version */}
          <div className="block md:hidden">
            <MobilePhoneFeature />
          </div>
          {/* Desktop version */}
          <div className="hidden md:block">
            <div className="absolute top-[75%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 scale-[0.65] origin-center transition-transform duration-300 ease-in-out">
              <div className="w-[562px] h-[440px] p-10 rounded-2xl overflow-hidden relative">
                {/* Top fade gradient - only on hover */}
                <div
                  className="absolute top-0 left-0 right-0 h-8 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10 pointer-events-none"
                  style={{ background: "linear-gradient(to bottom, var(--landing-bg-secondary-purple), transparent)" }}
                />
                {/* Bottom fade gradient - only on hover */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-8 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10 pointer-events-none"
                  style={{ background: "linear-gradient(to top, var(--landing-bg-secondary-purple), transparent)" }}
                />
                <div className="flex flex-col gap-6 transform group-hover:-translate-y-48 transition-transform duration-500 ease-in-out">
                  <div className="flex flex-col gap-2.5">
                    <div className="w-full flex flex-col gap-4">
                      <div className="flex justify-between items-end">
                        <div className="flex items-center gap-2">
                          <SparksSolid className="w-5 h-5 text-indigo-600" />
                          <div className="text-indigo-600 text-tag-md">Nowadays AI</div>
                        </div>
                        <div className="text-stone-300 text-body">00:01</div>
                      </div>
                      <div className="px-8 py-6 bg-white/80 rounded-2xl flex flex-col gap-4 overflow-hidden">
                        <div className="text-landing-primary text-body-xl">
                          Hi, my name is Victor. I sent over an RFP yesterday and was wondering if your team received
                          it.
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2.5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-in-out delay-100">
                    <div className="w-full flex flex-col gap-4">
                      <div className="flex justify-between items-end">
                        <div className="flex items-center gap-2">
                          <Phone className="w-5 h-5 text-neutral-400" />
                          <div className="text-landing-primary text-tag-md">Hotel Staff</div>
                        </div>
                        <div className="text-stone-300 text-body">00:05</div>
                      </div>
                      <div className="px-8 py-6 bg-white/80 rounded-2xl flex flex-col gap-4 overflow-hidden">
                        <div className="text-landing-primary text-body-xl">
                          No worries, let me take a look! What email did you send it to?
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2.5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-in-out delay-200">
                    <div className="w-full flex flex-col gap-4">
                      <div className="flex justify-between items-end">
                        <div className="flex items-center gap-2">
                          <SparksSolid className="w-5 h-5 text-indigo-600" />
                          <div className="text-indigo-600 text-tag-md">Nowadays AI</div>
                        </div>
                        <div className="text-stone-300 text-body">00:12</div>
                      </div>
                      <div className="px-8 py-6 bg-white/80 rounded-2xl flex flex-col gap-4 overflow-hidden">
                        <div className="text-landing-primary text-body-xl">
                          Thanks! I sent it to reservations@hotel.com
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Proposal preview for Proposal Translation section */}
      {title === "Proposal translation" && (
        <>
          {/* Mobile version */}
          <div className="block md:hidden">
            <MobileProposalFeature />
          </div>
          {/* Desktop version */}
          <div className="hidden md:block">
            <div className="absolute top-[90%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 scale-50 origin-center transition-all duration-300 ease-in-out group-hover:top-[85%]">
              <div className="w-[638px] h-[757px] bg-white/80 rounded-2xl overflow-hidden p-8">
                <div className="flex flex-col gap-12 h-full">
                  <div className="flex flex-col gap-2">
                    <div className="text-landing-primary text-tag-lg">Quote</div>
                    <div className="text-landing-primary text-metric-lg">$174,221.91</div>
                    <div className="text-landing-secondary text-body">Total after taxes & fees</div>
                  </div>
                  <div className="flex flex-col gap-4 flex-1">
                    <div className="text-landing-primary text-tag-lg">Details</div>
                    <div className="flex flex-col gap-6">
                      <div className="flex flex-col gap-4">
                        <div className="text-landing-secondary text-body">Guest Room Breakdown</div>
                        <div className="flex justify-between items-center">
                          <div className="text-landing-primary text-body">$305 nightly rate x 48 rooms x 8 nights</div>
                          <div className="text-landing-secondary text-body">$117,112</div>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="text-landing-primary text-body">Taxes & Fees</div>
                          <div className="text-landing-secondary text-body">$25,565</div>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="text-landing-primary text-tag-md">Total</div>
                          <div className="text-landing-primary text-tag-md">$142,685</div>
                        </div>
                      </div>
                      <div className="h-px bg-zinc-300" />
                      <div className="flex flex-col gap-4">
                        <div className="text-landing-secondary text-body">Meeting Room Breakdown</div>
                        <div className="flex justify-between items-center">
                          <div className="flex-1 h-6 bg-zinc-100 rounded mr-4" />
                          <div className="w-20 h-6 bg-zinc-100 rounded" />
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="flex-1 h-6 bg-zinc-100 rounded mr-4" />
                          <div className="w-20 h-6 bg-zinc-100 rounded" />
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="text-landing-primary text-tag-md">Total</div>
                          <div className="w-20 h-6 bg-zinc-100 rounded" />
                        </div>
                      </div>
                      <div className="h-px bg-zinc-300" />
                      <div className="flex flex-col gap-4">
                        <div className="text-landing-secondary text-body">Food & Beverage Breakdown</div>
                        <div className="flex justify-between items-center">
                          <div className="flex-1 h-6 bg-zinc-100 rounded mr-4" />
                          <div className="w-20 h-6 bg-zinc-100 rounded" />
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="flex-1 h-6 bg-zinc-100 rounded mr-4" />
                          <div className="w-20 h-6 bg-zinc-100 rounded" />
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="text-landing-primary text-tag-md">Total</div>
                          <div className="w-20 h-6 bg-zinc-100 rounded" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

interface ComparisonSectionProps {
  title: string;
  description: string;
  highlightText: string;
  traditionalLabel: string;
  nowadaysLabel: string;
  traditionalBarLength: string;
  nowadaysBarLength: string;
}

const ComparisonSection = ({
  title,
  description,
  highlightText,
  traditionalLabel,
  nowadaysLabel,
  traditionalBarLength,
  nowadaysBarLength,
}: ComparisonSectionProps) => {
  // Convert height classes to width percentages for horizontal bars
  const getBarWidth = (lengthClass: string) => {
    if (lengthClass === "h-60") return "75%"; // Traditional - shortened from 100%
    if (lengthClass === "h-8") return "15%"; // Nowadays - much shorter
    return "50%";
  };

  // Set up intersection observer for scroll-triggered animation
  const { ref, inView: inViewRef } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inViewRef) {
      setInView(true);
    }
  }, [inViewRef]);

  return (
    <div ref={ref} className="w-full lg:flex-1 lg:min-w-0 h-full relative overflow-hidden p-4 md:p-8">
      <div className="flex flex-col justify-start items-start gap-8 md:gap-12 h-full">
        <div className="flex flex-col gap-2">
          <div className="text-tag-xl text-landing-primary">{title}</div>
          <div className="flex flex-col">
            <div className="text-body text-landing-secondary">{description}</div>
            <div className="text-body text-landing-primary text-tag-md">{highlightText}</div>
          </div>
        </div>

        {/* Horizontal comparison bars */}
        <div className="flex flex-col justify-center items-start w-full gap-4">
          {/* Traditional bar */}
          <div className="w-full flex items-center gap-4 relative">
            <div className="w-full h-24 flex items-center relative">
              {/* Gradient background - grows with bar movement */}
              <motion.div
                className="h-full bg-linear-to-l from-stone-300 to-transparent absolute left-0"
                initial={{ width: "0%" }}
                animate={{ width: inView ? getBarWidth(traditionalBarLength) : "0%" }}
                transition={{
                  duration: 1.5,
                  ease: [0.16, 1, 0.3, 1],
                }}
              />
              {/* Bar strip - appears and moves to position */}
              <motion.div
                className="w-1 h-full bg-stone-400 absolute"
                initial={{ left: "0%", opacity: 0 }}
                animate={{
                  left: inView ? getBarWidth(traditionalBarLength) : "0%",
                  opacity: inView ? 1 : 0,
                }}
                transition={{
                  duration: 1.5,
                  ease: [0.16, 1, 0.3, 1],
                }}
                style={{ transform: "translateX(-2px)" }}
              />
            </div>
            {/* Label - moves with the bar */}
            <motion.div
              className="text-landing-secondary text-tag-md absolute flex flex-col items-start gap-2"
              initial={{ left: "16px", opacity: 0 }}
              animate={{
                left: inView ? `calc(${getBarWidth(traditionalBarLength)} + 16px)` : "16px",
                opacity: inView ? 1 : 0,
              }}
              transition={{
                duration: 1.5,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              <div className="text-landing-secondary text-tag-sm">Traditional</div>
              {traditionalLabel}
            </motion.div>
          </div>

          {/* Nowadays bar */}
          <div className="w-full flex items-center gap-4 relative">
            <div className="w-full h-24 flex items-center relative">
              {/* Gradient background - grows with bar movement */}
              <motion.div
                className="h-full bg-linear-to-l from-indigo-300 to-transparent absolute left-0"
                initial={{ width: "0%" }}
                animate={{ width: inView ? getBarWidth(nowadaysBarLength) : "0%" }}
                transition={{
                  duration: 1.5,
                  ease: [0.16, 1, 0.3, 1],
                }}
              />
              {/* Bar strip - appears and moves to position */}
              <motion.div
                className="w-1 h-full bg-indigo-400 absolute"
                initial={{ left: "0%", opacity: 0 }}
                animate={{
                  left: inView ? getBarWidth(nowadaysBarLength) : "0%",
                  opacity: inView ? 1 : 0,
                }}
                transition={{
                  duration: 1.5,
                  ease: [0.16, 1, 0.3, 1],
                }}
                style={{ transform: "translateX(-2px)" }}
              />
            </div>
            {/* Label with logo - moves with the bar */}
            <motion.div
              className="flex flex-col items-start gap-2 absolute"
              initial={{ left: "16px", opacity: 0 }}
              animate={{
                left: inView ? `calc(${getBarWidth(nowadaysBarLength)} + 16px)` : "16px",
                opacity: inView ? 1 : 0,
              }}
              transition={{
                duration: 1.5,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              <img src={nowadaysLogo.src} alt="Nowadays" className="h-5 w-auto filter grayscale opacity-60" />
              <div className="text-landing-primary text-tag-md">{nowadaysLabel}</div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Feature2 = () => {
  // Use unified modal manager
  const { openModal, closeModal, isModalOpen } = useModalManager();

  const handleBookDemo = () => {
    openModal("demo");
  };

  const handleReadEmails = () => {
    openModal("email");
  };

  const handleSeeProposalDetails = () => {
    openModal("proposal");
  };

  return (
    <>
      <div className="w-full relative py-12 md:py-24 flex justify-center">
        <div className="w-full md:p-16 md:rounded-3xl md:outline md:outline-4 md:-outline-offset-4 md:outline-(--landing-border-light) inline-flex justify-start items-center gap-2.5 overflow-hidden">
          <div className="w-full inline-flex flex-col justify-start items-start gap-12 md:gap-16">
            <div className="w-full flex flex-col justify-start items-start gap-6 md:gap-8">
              <div className="w-full flex flex-col justify-start items-start gap-4">
                <div className="w-full max-w-[530px] text-h2 text-landing-primary">
                  Why choose Nowadays over traditional options?
                </div>
                <div className="w-full max-w-[530px] text-body-lg text-landing-secondary">
                  Built by MIT engineers, we're the first AI-native platform purpose-built for corporate events. Our
                  proprietary tech and database of 400K+ venues—built in partnership with top venues and agencies—lets
                  us source faster, negotiate smarter, and simplify everything in one place.
                </div>
              </div>
              <Link href="/create">
                <Button variant="primary" color="primary" size="md">
                  Try our platform
                </Button>
              </Link>

              {/* ProductHunt badges */}
              <motion.div
                className="badges flex justify-center relative z-10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="flex space-x-4">
                  <a
                    href="https://www.producthunt.com/posts/nowadays-2?embed=true&utm_source=badge-top-post-badge&utm_medium=badge&utm_souce=badge-nowadays&#0045;2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="https://api.producthunt.com/widgets/embed-image/v1/top-post-badge.svg?post_id=824863&theme=neutral&period=daily&t=1738192821390"
                      alt="Nowadays - Streamlining&#0032;corporate&#0032;event&#0032;planning&#0032;with&#0032;AI | Product Hunt"
                      style={{ width: "250px", height: "54px" }}
                      width="250"
                      height="54"
                    />
                  </a>
                  <a
                    href="https://www.producthunt.com/posts/nowadays-2?embed=true&utm_source=badge-top-post-badge&utm_medium=badge&utm_souce=badge-nowadays&#0045;2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="https://api.producthunt.com/widgets/embed-image/v1/top-post-badge.svg?post_id=824863&theme=neutral&period=weekly&t=1738636113775"
                      alt="Nowadays - Streamlining&#0032;corporate&#0032;event&#0032;planning&#0032;with&#0032;AI | Product Hunt"
                      style={{ width: "250px", height: "54px" }}
                      width="250"
                      height="54"
                    />
                  </a>
                </div>
              </motion.div>
            </div>
            <div className="w-full h-[800px] lg:h-[500px] flex flex-col justify-start items-start">
              <div className="w-full h-full flex flex-col lg:flex-row justify-start items-start border border-landing-primary rounded-xl">
                <ComparisonSection
                  title="Venue sourcing time"
                  description="Manual sourcing takes days per event."
                  highlightText="Nowadays sources venues within minutes."
                  traditionalLabel="Multiple days"
                  nowadaysLabel="Within minutes"
                  traditionalBarLength="h-60"
                  nowadaysBarLength="h-8"
                />

                <div className="hidden md:block w-px self-stretch bg-border-landing-primary" />
                <div className="block md:hidden h-px w-full bg-border-landing-primary" />

                <ComparisonSection
                  title="Proposal comparison time"
                  description="Manual comparison takes 10+ hours per event."
                  highlightText="Nowadays AI analysis takes within seconds."
                  traditionalLabel="10+ hours"
                  nowadaysLabel="Within seconds"
                  traditionalBarLength="h-60"
                  nowadaysBarLength="h-8"
                />
              </div>
            </div>
            <div className="w-full flex flex-col justify-start items-center gap-6 md:gap-8">
              <div className="w-full md:text-center text-h3 text-landing-primary">
                Here's how we eliminate the busywork
              </div>
              <div className="w-full flex flex-col lg:flex-row justify-start items-stretch gap-4">
                <FeatureSection
                  title="AI email outreach"
                  description="Intelligent first-contact emails automatically personalized for each venue—so you get responses faster."
                  buttonText="Read emails"
                  leftOffset="22px"
                  onButtonClick={handleReadEmails}
                />
                <FeatureSection
                  title="AI phone calls"
                  description="AI-powered phone outreach gathers availability and answers follow-up questions so you don't have to."
                  buttonText="Book a demo"
                  leftOffset="22.34px"
                  onButtonClick={handleBookDemo}
                />
                <FeatureSection
                  title="Proposal translation"
                  description="No more messy PDFs or scattered emails. We turn every proposal into a clean, side-by-side comparison."
                  buttonText="See details"
                  leftOffset="21.66px"
                  onButtonClick={handleSeeProposalDetails}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Unified Modals */}
      <UnifiedModal
        type="email"
        isOpen={isModalOpen("email")}
        onClose={() => closeModal("email")}
        title="AI email examples"
        subtitle="Personalized outreach that gets responses"
        size="lg"
      />

      <UnifiedModal
        type="proposal"
        isOpen={isModalOpen("proposal")}
        onClose={() => closeModal("proposal")}
        title="Proposal Breakdown"
        subtitle="Clean, detailed proposal analysis"
        size="lg"
      />

      <BookADemoModal isOpen={isModalOpen("demo")} onClose={() => closeModal("demo")} />

      <UnifiedModal
        type="content"
        isOpen={isModalOpen("video")}
        onClose={() => closeModal("video")}
        title="Watch Demo Video"
        subtitle="See Nowadays in action"
        size="lg"
        closeOnBackdrop={true}
        closeOnEscape={true}
      >
        <div className="p-4">
          <iframe
            className="w-full h-[70vh] rounded-xl"
            src="https://www.youtube.com/embed/SS2bcogWkeE"
            title="Nowadays - AI for Event Planning (YC S23)"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          ></iframe>
        </div>
      </UnifiedModal>
    </>
  );
};
